import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import type { PollingSnapshot } from '@/types'
import { POLL_SCOPES } from '@/constants/polling'
import { Badge } from '@/components/ui/badge'
import { VoteShareBarCompact } from './VoteShareBar'

interface PollingSummaryCardProps {
  poll: PollingSnapshot
  playerPartyId: string
  currentTick: number
  targetName?: string
  onClick?: () => void
  className?: string
}

/**
 * Compact card displaying a poll summary in the list view.
 * Shows poll type, date, headline result, and click to expand.
 */
export function PollingSummaryCard({
  poll,
  playerPartyId,
  currentTick,
  targetName,
  onClick,
  className,
}: PollingSummaryCardProps) {
  const scopeConfig = POLL_SCOPES[poll.poll_type]
  const ticksAgo = currentTick - poll.tick
  const daysAgoText = ticksAgo === 0 ? 'Today' : ticksAgo === 1 ? '1d ago' : `${ticksAgo}d ago`

  // Find standings and player position
  const { leader, playerStanding, playerPosition } = useMemo(() => {
    const standings = poll.results.party_standings
    const sorted = [...standings].sort((a, b) => b.vote_share - a.vote_share)
    const leaderData = sorted[0]
    const playerData = standings.find((s) => s.party_id === playerPartyId)
    const position = sorted.findIndex((s) => s.party_id === playerPartyId) + 1

    return {
      leader: leaderData,
      playerStanding: playerData,
      playerPosition: position,
    }
  }, [poll.results.party_standings, playerPartyId])

  // Generate headline text
  const headline = useMemo(() => {
    if (!playerStanding) return `${leader.party_name} leads: ${leader.vote_share.toFixed(1)}%`

    const isLeading = playerStanding.party_id === leader.party_id
    if (isLeading) {
      return `You lead: ${playerStanding.vote_share.toFixed(1)}% (±${poll.margin_of_error.toFixed(1)}%)`
    }

    const positionSuffix = getOrdinalSuffix(playerPosition)
    return `Your position: ${playerPosition}${positionSuffix} at ${playerStanding.vote_share.toFixed(1)}%`
  }, [playerStanding, leader, playerPosition, poll.margin_of_error])

  // Secondary info about the leader (when player is not leading)
  const leaderInfo = useMemo(() => {
    if (!playerStanding || playerStanding.party_id === leader.party_id) return null
    return `${leader.party_name} leads: ${leader.vote_share.toFixed(1)}% (±${poll.margin_of_error.toFixed(1)}%)`
  }, [playerStanding, leader, poll.margin_of_error])

  return (
    <div
      onClick={onClick}
      className={cn(
        'p-3 rounded-lg border bg-card hover:bg-accent/50 cursor-pointer transition-colors',
        className
      )}
    >
      {/* Header row: poll type icon, label, and tick info */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <span className="text-lg" aria-hidden="true">
            {scopeConfig.icon}
          </span>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm">
                {scopeConfig.label} Poll
              </span>
              <Badge
                variant={poll.detail_level === 'detailed' ? 'default' : 'secondary'}
                className="text-xs px-1.5 py-0"
              >
                {poll.detail_level === 'detailed' ? 'Detailed' : 'Basic'}
              </Badge>
            </div>
            {targetName && poll.poll_type !== 'national' && (
              <span className="text-xs text-muted-foreground">{targetName}</span>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-sm text-muted-foreground">Tick {poll.tick}</div>
          <div className="text-xs text-muted-foreground">{daysAgoText}</div>
        </div>
      </div>

      {/* Sample size */}
      <div className="text-xs text-muted-foreground mb-2">
        Sample: {poll.sample_size.toLocaleString()}
      </div>

      {/* Headline result */}
      <div className="space-y-1">
        <div className="text-sm font-medium">{headline}</div>
        {leaderInfo && (
          <div className="text-xs text-muted-foreground">{leaderInfo}</div>
        )}
      </div>

      {/* Mini bar chart showing top 3 standings */}
      <div className="mt-3 space-y-1.5">
        {poll.results.party_standings
          .slice()
          .sort((a, b) => b.vote_share - a.vote_share)
          .slice(0, 3)
          .map((standing) => (
            <div key={standing.party_id} className="flex items-center gap-2">
              <div
                className="w-2 h-2 rounded-sm shrink-0"
                style={{ backgroundColor: standing.party_color }}
              />
              <span
                className={cn(
                  'text-xs truncate w-20',
                  standing.party_id === playerPartyId && 'font-semibold'
                )}
              >
                {standing.party_name}
                {standing.party_id === playerPartyId && ' ⭐'}
              </span>
              <div className="flex-1">
                <VoteShareBarCompact
                  partyColor={standing.party_color}
                  voteShare={standing.vote_share}
                  voteLow={standing.vote_share_low}
                  voteHigh={standing.vote_share_high}
                  isPlayerParty={standing.party_id === playerPartyId}
                />
              </div>
              <span className="text-xs tabular-nums w-10 text-right">
                {standing.vote_share.toFixed(1)}%
              </span>
            </div>
          ))}
      </div>
    </div>
  )
}

function getOrdinalSuffix(n: number): string {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return s[(v - 20) % 10] || s[v] || s[0]
}
